/**
 * @fileoverview Ad Spends service with query serialization and Zod validation.
 * @module features/ad-spends/api/adService
 */

import apiClient from '@/lib/apiClient'
import {
  adSpendsParamsSchema,
  paginatedAdSpendsSchema,
  type AdSpendsParams,
  type AdSpendsResponse,
} from '@/features/ad-spends/schemas/adSpendsSchema'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'

/**
 * Normalize a date-like value into YYYY-MM-DD (local) string as used across the app.
 */
function normalizeDateParam(value: Date | string | null | undefined): string | undefined {
  if (!value) return undefined
  if (value instanceof Date) return toDateOnlyStringLocal(value)
  return value
}

/**
 * Build query params for axios. Arrays are serialized as comma-separated lists
 * to match patterns used by salesService, while remaining compatible with the backend.
 */
function buildQueryParams(params: AdSpendsParams): Record<string, unknown> {
  const query: Record<string, unknown> = {}

  if (typeof params.page === 'number') query.page = params.page
  if (typeof params.size === 'number') query.size = params.size
  if (params.sort) query.sort = params.sort
  if (params.direction) query.direction = params.direction

  const start = normalizeDateParam(params.startDate)
  const end = normalizeDateParam(params.endDate)
  if (start) query.startDate = start
  if (end) query.endDate = end

  if (params.skus && params.skus.length > 0) {
    query.skus = params.skus.join(',')
  }
  if (params.countries && params.countries.length > 0) {
    query.countries = params.countries.join(',')
  }
  if (params.platforms && params.platforms.length > 0) {
    query.platforms = params.platforms.join(',')
  }

  return query
}

/**
 * Ad Spends service API.
 */
export const adService = {
  /**
   * Fetch paginated ad spends with filters, pagination and sorting.
   *
   * @param params AdSpendsParams
   * @returns Parsed and validated AdSpendsResponse
   * @throws Error when network fails or validation fails
   * @example
   * ```ts
   * const res = await adService.getAdSpends({ page: 0, size: 50, sort: "date", direction: "desc" });
   * ```
   */
  async getAdSpends(params: AdSpendsParams = {}): Promise<AdSpendsResponse> {
    // Validate input params shape early (fail fast)
    const parsedParams = adSpendsParamsSchema.parse(params)
    const queryParams = buildQueryParams(parsedParams)
    const response = await apiClient.get('/ad-spends', { params: queryParams })

    const parsed = paginatedAdSpendsSchema.safeParse(response.data)
    if (!parsed.success) {
      // Keep details out of UI; log for diagnostics and throw a clean message
      console.error('[adService] Validation error:', parsed.error.flatten())
      throw new Error('API validation failed for AdSpendsResponse')
    }
    return parsed.data
  },
}
